import LegalLink from 'components/LegalLink';
import {
  RadioAccordion,
  RadioAccordionOption,
} from 'components/RadioAccordion/RadioAccordion';
import RequiredIndicator from 'components/RequiredIndicator';
import { useAccount } from 'contexts/account';
import {
  useEnabledPaymentMethods,
  usePaymentMethodsWidgetConsumer,
} from 'contexts/enabledPaymentMethods';
import { useUser } from 'contexts/user';
import { CardFrame, Frames } from 'frames-react';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from 'semantic';
import { Checkbox, Message } from 'semantic-ui-react';
import { Account } from 'types/account';
import {
  AccountHolder,
  AuthorizeTokenizationRequest,
  AuthorizeTokenizationResponse,
  StoreTokenizationRequest,
  StoreTokenizationResponse,
} from 'types/payment-method-tokanization';
import { UserPaymentMethod } from 'types/PaymentMethods';
import request from 'utils/api/request';
import { CHECKOUT_COM_PUBLIC_KEY } from 'utils/env';

export type PaymentMethodsWidgetCallbacks = {
  authorizeActiveOption: () => Promise<AuthorizeResponse>;
};

type PaymentMethodOptionCallback = {
  authorize: () => Promise<AuthorizeResponse>;
};

type AuthorizeResponse = {
  redirectUrl?: string;
  success: boolean;
};

type Props = {
  redirectUrl?: string;
  hideTriggerButtons?: boolean;
};
export function PaymentMethodsWidget({
  redirectUrl = window.location.href,
  hideTriggerButtons,
}: Props) {
  const { t } = useTranslation();
  const { user, provider } = useUser();
  const { account } = useAccount();
  const { enabledPaymentMethods } = useEnabledPaymentMethods();
  const [error, setError] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const idealFormRef = useRef<PaymentMethodOptionCallback>(null);
  const ccFormRef = useRef<PaymentMethodOptionCallback>(null);

  const pmRef = usePaymentMethodsWidgetConsumer();

  const onAuthorizeActiveOptionRequest =
    async (): Promise<AuthorizeResponse> => {
      if (activeOption === '') {
        setError(
          t(
            'settings.paymentMethods.selectPaymentMethod',
            'Please select a payment method.'
          )
        );
        return { success: false };
      }
      // In case it's a CC we will submit the form and we can detect if it's visa or mastercard later.
      if (activeOption === 'credit-card') {
        const authResponse = await ccFormRef.current?.authorize();
        if (authResponse) {
          return authResponse;
        } else {
          return { success: false };
        }
      }

      const activePaymentMethod = enabledPaymentMethods.find(
        (e) => e.id === activeOption
      );

      if (!activePaymentMethod) {
        return { success: false };
      }

      if (activePaymentMethod.paymentMethod === 'ideal') {
        const idealAuthResponse = await idealFormRef.current?.authorize();
        if (idealAuthResponse) {
          return idealAuthResponse;
        } else {
          return { success: false };
        }
      } else {
        throw new Error(
          `Payment Type ${activePaymentMethod.paymentMethod} Not Implemented`
        );
      }
    };

  useImperativeHandle(pmRef?.ref, () => ({
    authorizeActiveOption: onAuthorizeActiveOptionRequest,
  }));

  const activeUrl = new URL(redirectUrl);

  const [activeOption, setActiveOption] = useState<string>('');

  useEffect(() => {
    if (activeUrl.searchParams.get('payment-method-id')) {
      setActiveOption(activeUrl.searchParams.get('payment-method-id')!);
      return;
    }
    if (enabledPaymentMethods.length === 1) {
      setActiveOption(enabledPaymentMethods[0].id);
    }
  }, [enabledPaymentMethods]);

  if (!user || !provider || !account) {
    return <div />;
  }

  const options: RadioAccordionOption[] = [];

  if (enabledPaymentMethods?.find((e) => e.paymentType === 'card')) {
    options.push({
      header: t('settings.paymentMethods.creditCard', 'Credit Card'),
      id: 'credit-card',
      content: (
        <CardForm
          accountBillingCountry={account?.billing?.countryCode || ''}
          paymentMethods={enabledPaymentMethods || []}
          redirectUrl={redirectUrl}
          ref={ccFormRef}
        />
      ),
    });
  }

  const idealPaymentMethod = enabledPaymentMethods?.find(
    (e) => e.paymentMethod === 'ideal'
  );
  if (idealPaymentMethod) {
    options.push({
      header: (
        <div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <IdealLogo />

            <strong style={{ marginLeft: '1rem' }}>
              {t('settings.paymentMethods.iDeal', 'iDEAL')}
            </strong>
          </div>
          <br />
          {t('settings.paymentMethods.iDealSuptitle', 'SEPA with iDEAL')}
        </div>
      ),
      id: idealPaymentMethod.id,
      content: (
        <IDealForm
          paymentMethodId={idealPaymentMethod.id}
          account={account}
          ref={idealFormRef}
          redirectUrl={redirectUrl}
        />
      ),
    });
  }

  return (
    <>
      <RadioAccordion
        options={options}
        onOptionSelected={(e) => setActiveOption(e)}
        optionSelected={activeOption}
      />
      {error && <Message error>{error}</Message>}
      {!hideTriggerButtons && (
        <Button
          disabled={isRedirecting}
          onClick={() => {
            setIsRedirecting(true);
            onAuthorizeActiveOptionRequest()
              .then((result) => {
                if (!result.success) {
                  setIsRedirecting(false);
                  return;
                }

                if (result.redirectUrl) {
                  setIsRedirecting(true);
                  window.location.href = result.redirectUrl;
                  return;
                }
              })
              .catch((e) => {
                console.error('Error authorizing payment method', e);
                setIsRedirecting(false);
                setError(
                  t(
                    'settings.paymentMethods.errorAuthorizingPaymentMethod',
                    'There was an error authorizing the payment method.'
                  )
                );
              });
          }}>
          {t(
            'settings.paymentMethods.replacePaymentMethod',
            'Replace Payment Method'
          )}
        </Button>
      )}
    </>
  );
}

type IDealFormProps = {
  paymentMethodId: string;
  account: Account;
  redirectUrl: string;
};
const IDealForm = forwardRef<PaymentMethodOptionCallback, IDealFormProps>(
  ({ paymentMethodId, account, redirectUrl }, ref) => {
    const { t } = useTranslation();
    const [sepaTermsAccepted, setSepaTermsAccepted] = useState(false);
    const [showSepaNotAcceptedError, setShowSepaNotAcceptedError] =
      useState(false);

    const authorizeIDeal = () => {
      const currentUrl = new URL(redirectUrl);
      currentUrl.searchParams.set('payment-method-id', paymentMethodId);
      return request({
        path: `/1/accounts/self/authorize-tokenization`,
        method: 'POST',
        body: {
          paymentMethodId: paymentMethodId,
          currencyCode: account?.billingCurrency,
          tokenizationData: {
            case: 'bankTransfer',
            value: {
              redirectUrl: currentUrl.toString(),
            },
          },
        },
      });
    };

    useImperativeHandle(ref, () => ({
      authorize: async () => {
        if (!sepaTermsAccepted) {
          setShowSepaNotAcceptedError(true);
          return { success: false };
        }

        try {
          const response = await authorizeIDeal();
          return {
            success: true,
            redirectUrl: response.data?.action?.value?.url,
          };
        } catch (err) {
          return { success: false };
        }
      },
    }));

    return (
      <>
        <p>
          {t(
            'settings.paymentMethods.idealDescription1',
            'We are going to establish and verify your bank account through iDeal.'
          )}
        </p>
        <p>
          {t(
            'settings.paymentMethods.idealDescription2',
            'Please choose your bank in next step and follow authorization and payment process.'
          )}
        </p>
        <div style={{ margin: '1rem 0', maxWidth: 335 }}>
          <Checkbox
            onChange={(_, { checked }) => {
              setSepaTermsAccepted(!!checked);
            }}
            label={
              <label>
                <Trans i18nKey="settings.organization.billing.sepaTerms">
                  I accept the&nbsp;
                  <LegalLink type="sepa">
                    SEPA authorization and terms
                  </LegalLink>
                </Trans>
                <RequiredIndicator />
              </label>
            }
          />
        </div>

        {showSepaNotAcceptedError && (
          <p style={{ color: 'red' }}>
            {t(
              'settings.paymentMethods.sepaTermsNotAccepted',
              'You must accept the SEPA terms to continue.'
            )}
          </p>
        )}
      </>
    );
  }
);

type CardFormProps = {
  accountBillingCountry: string;
  paymentMethods: UserPaymentMethod[];
  redirectUrl: string;
};
const CardForm = forwardRef<PaymentMethodOptionCallback, CardFormProps>(
  ({ paymentMethods, accountBillingCountry, redirectUrl }, ref) => {
    const { t } = useTranslation();
    const [error, setError] = useState<string>();
    const submitCard = async (token: string, paymentMethodId: string) => {
      const currentUrl = new URL(redirectUrl);
      currentUrl.searchParams.set('payment-method-id', paymentMethodId);
      return request<
        AuthorizeTokenizationResponse,
        AuthorizeTokenizationRequest
      >({
        path: `/1/accounts/self/authorize-tokenization`,
        method: 'POST',
        body: {
          currencyCode: 'EUR',
          paymentMethodId,
          tokenizationData: {
            case: 'card',
            value: {
              cardToken: token,
              redirectUrl: currentUrl.toString(),
            },
          },
        },
      });
    };

    useImperativeHandle(ref, () => ({
      authorize: async () => {
        try {
          const tokenizedCard = await Frames.submitCard();
          console.log('Card submitted', tokenizedCard);
          const id = paymentMethods.find((e) => {
            return (
              e.paymentMethod?.toLowerCase() ===
                tokenizedCard.scheme?.toLowerCase() &&
              e.countryCode?.toLowerCase() ===
                accountBillingCountry.toLowerCase()
            );
          })?.id;

          if (!id) {
            console.error(
              'Payment method not found',
              paymentMethods,
              tokenizedCard
            );
            return { success: false };
          }

          const submitCardResult = await submitCard(tokenizedCard.token, id!);
          if (submitCardResult.data?.action?.value?.url) {
            return {
              success: true,
              redirectUrl: submitCardResult.data.action.value.url,
            };
          } else {
            return { success: true };
          }
        } catch (err) {
          setError(
            t(
              'settings.paymentMethods.errorSubmittingCard',
              'There was an error submitting the card.'
            )
          );
          return { success: false };
        }
      },
    }));

    return (
      <div>
        <Frames
          config={{
            // TODO: Set this via Env vars when CC payment method is enabled (for now out of scope)
            publicKey: CHECKOUT_COM_PUBLIC_KEY,
            localization: {
              cardNumberPlaceholder: 'Card number',
              expiryMonthPlaceholder: 'MM',
              expiryYearPlaceholder: 'YY',
              cvvPlaceholder: 'CVV',
            },
            style: {},
          }}>
          <CardFrame />
        </Frames>
        {error && <Message error>{error}</Message>}
      </div>
    );
  }
);

const cleanUpUrl = () => {
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.delete('cko-session-id');
  currentUrl.searchParams.delete('payment-method-id');
  window.history.replaceState({}, '', currentUrl.toString());
};

/**
 * Generates AccountHolder Info
 * @throws {Error} if the account billing info is not complete
 **/
const generateAccountHolder = (account: Account): AccountHolder => {
  if (!account.billing) {
    throw new Error('Account billing info is not complete');
  }
  if (!account.contact) {
    throw new Error('Account contact info is not complete');
  }
  if (!account.contact?.firstName) {
    throw new Error('Account billing first name is not complete');
  }
  if (!account.contact?.lastName) {
    throw new Error('Account billing last name is not complete');
  }
  if (!account.billing?.address) {
    throw new Error('Account billing address is not complete');
  }
  if (!account.billing?.city) {
    throw new Error('Account billing city is not complete');
  }
  if (!account.billing?.postalCode) {
    throw new Error('Account billing postal code is not complete');
  }
  if (!account.billing?.countryCode) {
    throw new Error('Account billing country code is not complete');
  }
  return {
    firstName: account.contact.firstName,
    lastName: account.contact.lastName,
    billingAddress: {
      addressLine1: account.billing.address,
      addressLine2: account.billing.addressLine2,
      city: account.billing.city,
      postalCode: account.billing.postalCode,
      countryCode: account.billing.countryCode,
    },
  };
};

const createStoreTokenizationRequest = (
  account: Account,
  enabledPaymentMethods: UserPaymentMethod[],
  ckoSessionId: string,
  paymentMethodId: string
): StoreTokenizationRequest | undefined => {
  // We are not in a redirect situation, no errors are there
  if (!ckoSessionId && !paymentMethodId) {
    return undefined;
  }

  // We are missing a param from Checkout, so we should report that immediatly!
  if (!ckoSessionId) {
    console.error('Cko session id is missing');
    return;
  }

  // We are missing a param from our redirectUrl, it's a but we should fix ASAP
  if (!paymentMethodId) {
    console.error('Payment method id is missing');
    return;
  }

  // we are in a redirect situation, but account is not there yet
  if (!account) {
    return undefined;
  }

  const accountCurrencyCode = account.billingCurrency;
  if (accountCurrencyCode === undefined) {
    console.error('Currency code is undefined');
    return undefined;
  }

  const accountCountryCode = account.billing?.countryCode;
  if (accountCountryCode === undefined) {
    console.error('Country code is undefined');
    return undefined;
  }

  const selectedPaymentMethod = enabledPaymentMethods?.find((e) => {
    return e.id === paymentMethodId;
  });

  if (!selectedPaymentMethod) {
    console.error('Payment method was not found');
    return undefined;
  }

  if (!selectedPaymentMethod.enabled) {
    console.error('Payment method is not enabled');
    return undefined;
  }

  switch (selectedPaymentMethod.paymentType) {
    case 'card':
      return {
        paymentMethodId: paymentMethodId,
        currencyCode: accountCurrencyCode,
        countryCode: accountCountryCode.toUpperCase(),
        tokenizationData: {
          case: 'card',
          value: {
            paymentProviderReference: ckoSessionId,
          },
        },
      };
    case 'bank-transfer':
      return {
        paymentMethodId: paymentMethodId,
        currencyCode: accountCurrencyCode,
        countryCode: accountCountryCode,
        tokenizationData: {
          case: 'bankTransfer',
          value: {
            paymentProviderReference: ckoSessionId,
            accountHolder: generateAccountHolder(account),
          },
        },
      };
    case 'direct-debit':
      return {
        paymentMethodId: paymentMethodId,
        currencyCode: accountCurrencyCode,
        countryCode: accountCountryCode,
        tokenizationData: {
          case: 'directDebit',
          value: {
            paymentProviderReference: ckoSessionId,
            accountHolder: generateAccountHolder(account),
          },
        },
      };
    default:
      throw new Error('Unknown payment type');
  }
};

/**
 *
 * This method will try saving a payment instrument if the current link is a redirect result
 * from the PSP. as in you press iDeal, you authorize and you come back from that with a
 * token and a payment method id.
 *
 * returns true if the payment instrument was stored
 * returns false if no cko-session-id or payment-method-id was found
 * throws in case of an error storing the payment instrument.
 *
 * @param account - the account the user belongs to
 * @param enabledPaymentMethods - the payment methods that are enabled for the user (comes back from paybridge)
 *
 **/
export const tryStoringPaymentInstrument = async (
  account: Account,
  enabledPaymentMethods: UserPaymentMethod[]
): Promise<boolean> => {
  if (!enabledPaymentMethods.length) {
    return false;
  }
  const currentUrl = new URL(window.location.href);
  const ckoSessionId = currentUrl.searchParams.get('cko-session-id');
  const paymentMethodId = currentUrl.searchParams.get('payment-method-id');

  if (!ckoSessionId || !paymentMethodId) {
    return false;
  }

  const storeTokenizationRequest = createStoreTokenizationRequest(
    account,
    enabledPaymentMethods,
    ckoSessionId,
    paymentMethodId
  );

  cleanUpUrl();

  await request<{ data: StoreTokenizationResponse }, StoreTokenizationRequest>({
    path: '/1/accounts/self/store-tokenization',
    method: 'POST',
    body: storeTokenizationRequest,
  });

  return true;
};

function IdealLogo() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 93 80"
      height={24}
      width={24}>
      <path
        fill="#C06"
        d="M25.129 13.064V71.33h25.366c23.042 0 33.033-13.02 33.033-31.39 0-17.595-9.99-31.27-33.033-31.27H29.522a4.393 4.393 0 0 0-4.393 4.394Z"></path>
      <path
        fill="#000"
        d="M6.59 2.196A4.398 4.398 0 0 0 2.196 6.59v66.82a4.398 4.398 0 0 0 4.394 4.394h40.992c27.397 0 42.487-13.458 42.487-37.894 0-32.815-26.62-37.714-42.487-37.714H6.59ZM47.582 80H6.59A6.597 6.597 0 0 1 0 73.41V6.59A6.597 6.597 0 0 1 6.59 0h40.992C86.46 0 92.265 25.004 92.265 39.91 92.265 65.763 76.397 80 47.582 80Z"></path>
      <path
        fill="#fff"
        d="M33.845 43.42c.441 0 .87-.072 1.287-.214.416-.143.786-.38 1.11-.712.326-.33.586-.763.78-1.296.195-.532.293-1.183.293-1.95 0-.702-.069-1.335-.204-1.9a3.782 3.782 0 0 0-.673-1.453 2.999 2.999 0 0 0-1.237-.926c-.515-.215-1.148-.322-1.901-.322h-2.184v8.774h2.73Zm.215-11.346c.896 0 1.732.144 2.504.43a5.438 5.438 0 0 1 2.008 1.285c.566.572 1.007 1.287 1.327 2.145.317.858.477 1.865.477 3.022 0 1.014-.13 1.95-.39 2.808-.26.856-.653 1.598-1.179 2.221a5.52 5.52 0 0 1-1.97 1.473c-.786.358-1.712.536-2.777.536h-6.005v-13.92h6.005Zm18.756.001v2.572h-7.349v2.983h6.744v2.379h-6.744v3.412h7.505v2.573H42.406v-13.92h10.41Zm10.656 8.538-1.755-5.108h-.039l-1.812 5.108h3.606Zm-.175-8.539 5.204 13.92h-3.177l-1.053-3.1h-5.206l-1.091 3.1h-3.08l5.264-13.92h3.139Zm10.19.001V43.42h6.784v2.573h-9.845v-13.92h3.06Z"></path>
      <path
        fill="#000"
        d="M20.088 39.034a6.421 6.421 0 1 1-12.842 0 6.421 6.421 0 0 1 12.842 0ZM18.53 71.33c-5.373 0-9.73-4.356-9.73-9.73v-7.588a4.865 4.865 0 0 1 9.73 0V71.33Z"></path>
    </svg>
  );
}
